import { useEffect, useState } from "react";
import { fetch } from "./api-wrapper";

/**
 * XPEN-3481: which mileage travel types the report owner is allowed to pick.
 *
 * An administrator can reserve a travel type for a department or an Aderant rank
 * (the job-seniority band some firms use). `GET /expenses/mileage/travel-types`
 * applies that rule and returns only what the owner may use; `GET /expense_rates/`
 * returns the whole administrator configuration and applies nothing.
 *
 * The ids are used to *filter* the list the screens already hold, rather than to
 * replace it. The filtered endpoint deliberately exposes only picker fields, and
 * the expense editor needs `imputation_id` off the chosen type to stamp its
 * accounting category — so the full row has to keep coming from the rates config.
 *
 * **Loading and unknown are different answers.** Both leave the ids unavailable,
 * but only one of them should leave a picker usable. While the answer is still in
 * flight the caller can hold the picker back; once a request has genuinely failed
 * the caller must fall open, because hiding every travel type would strand a
 * traveller who has done nothing wrong — and the server refuses a restricted pick
 * either way. Collapsing the two meant the picker showed restricted types for the
 * whole round trip, on every screen open.
 *
 * @param {string|null|undefined} ownerUserId - The report owner. Trips can be
 *   filed by an assistant, and the server judges the owner's departments, not the
 *   requester's, so the screens must ask about the same person. Falsy means the
 *   owner is not known yet: nothing is requested, and the result is `loading`.
 * @returns {{allowedIds: Set<string>|null, loading: boolean}} `allowedIds` is null
 *   until an answer arrives and stays null if the request failed; `loading` is
 *   true while no answer has come back yet.
 */
export function useAccessibleTravelTypeIds(ownerUserId) {
    const [state, setState] = useState({ allowedIds: null, loading: true });

    useEffect(() => {
        // Nothing to ask until the report's owner is known. Asking anyway would
        // answer for the signed-in user instead, briefly applying an assistant's
        // own departments to someone else's picker. Callers that have an owner but
        // no travel type on screen pass undefined for the same reason — there is
        // nothing to wait for, so this is not "loading".
        if (!ownerUserId) {
            setState({ allowedIds: null, loading: false });
            return undefined;
        }

        // Drop the previous owner's answer before asking about this one. The
        // cancel flag stops a late response being written, but without this the
        // *old* set stays on screen while the new request is in flight — which is
        // the very thing this hook exists to avoid, just one owner later.
        setState({ allowedIds: null, loading: true });

        let cancelled = false;
        fetch(
            "/expenses/mileage/travel-types",
            "GET",
            { user_id: ownerUserId },
            (response) => {
                if (cancelled) return;
                const types = response?.data?.travel_types;
                // A response without a list is "unknown", not "allowed nothing".
                // Treating a shape we did not expect as an empty allow-list would
                // empty the picker for everyone — including against an older API
                // that does not serve this endpoint yet.
                setState({
                    allowedIds: Array.isArray(types) ? new Set(types.map((t) => t.id)) : null,
                    loading: false,
                });
            },
            () => {
                // Answered, badly. Null ids plus loading false is the fail-open
                // case: every travel type stays on offer and the server is the
                // boundary.
                if (!cancelled) setState({ allowedIds: null, loading: false });
            }
        );
        return () => { cancelled = true; };
    }, [ownerUserId]);

    return state;
}

/**
 * Keep only the travel types the owner may pick.
 *
 * A null `allowedIds` means the allowed list is not known. The full list is
 * returned in that case: hiding everything would strand the traveller, and the
 * server rejects a restricted pick regardless. Callers that can wait should use
 * the hook's `loading` flag to hold the picker back rather than relying on this.
 */
export function filterToAccessibleTravelTypes(travelTypes, allowedIds) {
    const types = travelTypes ?? [];
    if (!allowedIds) return types;
    return types.filter((t) => allowedIds.has(t.id));
}
